import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import MetricCard from "../../../common/Card/MetricCard";
import CommonTable from "../../../common/table/ComonTable";
import CustomSelect from "../../../common/formfields/CustomSelect";
import DeleteModal from "../../../common/commonmodal/DeleteModal";
import apiServices from "../../../../services/apiService";
import { toast } from "react-toastify";

import {
    faUsers,
    faRocket,
    faHourglassHalf,
    faDownload,
    faUserPlus,
    faFileContract
} from "@fortawesome/free-solid-svg-icons";
import { exportToExcel } from "../../../../utils/excelUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import storageService from "../../../../utils/storageService";

import { getInitials, getRandomColor } from "../../../../utils/commonFunctions";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import ExpandedTenancyRow from "./ExpandedTenancyRow";

const ManageUsers = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const siteId = user?.siteId;
    const unitId = user?.branchorunitID;
    const roleId = Number(user?.roleId || user?.RoleId);

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        userId: null,
        userName: ""
    });

    const fetchTenancyData = async (unitId) => {
        if (!unitId) return null;
        try {
            const response = await apiServices({
                method: 'GET',
                url: `/CommUnitTenancy/getTenancyByUnitId/${unitId}`
            });

            if (response?.success) {
                return response?.data?.Data[0];
            }
        } catch (error) {
            console.error("Error fetching tenancy data:", error);
        }
        return null;
    };

    const [statusFilter, setStatusFilter] = useState("All");

    const statusOptions = [
        { label: "All Status", value: "All" },
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" }
    ];

    const fetchUsers = useCallback(async (page = pagination.pageNumber, customPageSize = null) => {
        setLoading(true);
        try {
            const pageSize = customPageSize || pagination.pageSize;

            let url = `/CommUserDetails/getUserDetails?PageNumber=${page}&PageSize=${pageSize}`;
            if (roleId === 3) {
                if (siteId) url += `&SiteId=${siteId}`;
            } else if (roleId === 4) {
                if (siteId) url += `&SiteId=${siteId}`;
                if (unitId) url += `&UnitId=${unitId}`;
            }

            const response = await apiServices({
                method: "GET",
                url: url
            });
            if (response.success || response.data?.success) {
                const resData = response?.data?.Commuserdetailsdata || response?.data?.data || response?.data || [];
                setUsers(resData);

                const extractedTotal = response?.data?.totalCount || response?.data?.totalRecords || response?.data?.total || response?.totalRecords || resData.length || 0;

                setPagination(prev => ({
                    ...prev,
                    pageNumber: Number(page),
                    pageSize: Number(pageSize),
                    totalRecords: extractedTotal,
                    totalPages: Math.ceil(extractedTotal / pageSize) || 1
                }));
                setLoading(false)
            }
        } catch (error) {
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize]);

    const handleLimitChange = (newLimit) => {
        fetchUsers(1, newLimit);
    };

    const onPageChange = (page) => {
        fetchUsers(page);
    };

    const handleExport = () => {
        if (!users || users.length === 0) {
            toast.info("No data to export");
            return;
        }

        const excelHeaders = [
            { label: "First Name", key: "FirstName" },
            { label: "Last Name", key: "LastName" },
            { label: "Email Address", key: "Email" },
            { label: "Mobile Number", key: "MobileNumber" },
            { label: "Date of Birth", key: "dob" },
            { label: "Age", key: "Age" },
            { label: "Gender", key: "Gender" },
            { label: "Blood Group", key: "EmployeeBloodGroup" },
            { label: "Role", key: "roleName" },
            { label: "Department", key: "DepartmentName" },
            { label: "Designation", key: "Designation" },
            { label: "Date of Joining", key: "doj" },
            { label: "Address", key: "UserAddress" },
            { label: "Emergency Contact", key: "EmployeeEmergencyContact" },
            { label: "Status", key: "statusLabel" },
        ];

        const dataToExport = users.map(item => ({
            ...item,
            dob: item.DateOfBirth ? item.DateOfBirth.split('T')[0] : "-",
            doj: item.DateOfJoining ? item.DateOfJoining.split('T')[0] : "-",
            roleName: item.RoleName || "-",
            statusLabel: item.IsActive || "Inactive"
        }));

        exportToExcel(dataToExport, excelHeaders, "User_Records");
    };

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleAddNew = () => {
        storageService.removeItem("userId");
        storageService.removeItem("isPreview");
        navigate("/zentcomm/user-management/form");
    };

    const handleEdit = (user) => {
        navigate("/zentcomm/user-management/form", {
            state: {
                userId: user.Id,
                basicInfo: {
                    first_Name: user.FirstName,
                    last_Name: user.LastName,
                    email_Id: user.Email,
                    mobile_No: user.MobileNumber,
                    designation: user.Designation,
                },
                tenantData: user,
                roleData: {
                    roleId: user.RoleId,
                    siteId: user.siteId,
                    unitId: user.UnitId,
                },
                isPreview: false
            }
        });
    };

    const confirmDelete = async () => {
        try {
            const response = await apiServices({
                method: "DELETE",
                url: `/CommUserDetails/deleteUserDetails/${deleteModal.userId}`,
            });
            if (response.success || response.data?.success) {
                toast.success("User deleted successfully");
                fetchUsers();
            } else {
                toast.error(response.message || "Failed to delete user");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("An error occurred during deletion");
        } finally {
            setDeleteModal({ isOpen: false, userId: null, userName: "" });
        }
    };

    const metrics = [
        { title: "Total Users", value: users.length, icon: faUsers },
        { title: "Active Users", value: users.filter(u => u.IsActive === 'Active').length, icon: faRocket },
        { title: "Inactive/Draft", value: users.filter(u => (u.IsActive || 'Inactive') === 'Inactive').length, icon: faHourglassHalf },
    ];

    const tableHeaders = [
        { label: "No", key: "sno" },
        { label: "USER NAME", key: "name", align: "left" },
        { label: "MOBILE NUMBER", key: "contact" },
        { label: "ROLE", key: "role" },
        { label: "DESIGNATION", key: "designation" },
        { label: "STATUS", key: "status" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderTableCell = (key, row, idx) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs text-[#64748B] dark:text-textsecondary">{(pagination.pageNumber - 1) * pagination.pageSize + idx + 1}</span>;
            case "name":
                const fullName = `${row.FirstName || ''} ${row.LastName || ''}`.trim();
                const initials = getInitials(fullName);
                const color = getRandomColor(fullName);
                return (
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs uppercase ${color.bg} ${color.text}`}>
                            {row.UserImage ? <img src={row.UserImage} alt="User" className="w-full h-full rounded-lg object-cover" /> : initials}
                        </div>
                        <div className="flex flex-col text-left">
                            <span className="font-semibold text-gray-800 dark:text-textprimary tracking-tight">{fullName}</span>
                            {row.Id && <span className="text-[10px] text-gray-400 dark:text-textsecondary">ID : {row.Id}</span>}
                        </div>
                    </div>
                );
            case "contact":
                return <span className="text-[#64748B] dark:text-textsecondary font-medium">{row.MobileNumber || "-"}</span>;
            case "role":
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50">
                        {row.RoleName || row.roleName || "—"}
                    </span>
                );
            case "designation":
                return <span className="font-medium text-xs text-[#64748B] dark:text-textsecondary">{row.Designation || "—"}</span>;
            case "status":
                return (
                    <span className={`px-3 py-1 rounded-lg font-bold text-xs ${row.IsActive === 'Active' ? "bg-[#DCFCE7] text-[#166534]" : "bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-300"
                        }`}>
                        {row.IsActive || "Inactive"}
                    </span>
                );
            case "actions":
                return null;
            default:
                return row[key] || "-";
        }
    };

    const tableActions = (
        <div className="flex items-center gap-3">
            <div className="w-[160px]">
                <CustomSelect
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === statusFilter)}
                    onChange={(option) => {
                        if (option) {
                            setStatusFilter(option.value);
                            setPagination(prev => ({ ...prev, pageNumber: 1 }));
                        }
                    }}
                    placeholder="Select Status"
                />
            </div>
            <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] dark:bg-[#1B1B1D] border border-[#E2E8F0] dark:border-[#363638] text-[#64748B] dark:text-textsecondary text-xs font-bold rounded-lg hover:bg-white dark:hover:bg-[#2B2B2D] hover:text-[#1E293B] dark:hover:text-textprimary hover:border-[#1E293B] dark:hover:border-accentborder transition-all duration-200 shadow-sm"
            >
                <FontAwesomeIcon icon={faDownload} className=" text-sm" />
                <span>Export</span>
            </button>
        </div>
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading={`User Management`}
                subHeading="Oversee user accounts, roles, and system access levels for your organization."
                primaryBtnText="Add New User"
                primaryBtnIcon={faUserPlus}
                PrimaryOnClick={handleAddNew}

            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ">
                {metrics.map((metric, index) => (
                    <MetricCard
                        key={index}
                        title={metric.title}
                        value={metric.value}
                        icon={metric.icon}
                        className="w-full"
                    />
                ))}
            </div>

            <div className="shrink-0 flex-1 flex flex-col">
                {(() => {
                    const filteredUsers = users.filter(u => {
                        if (statusFilter === "All") return true;
                        return (u.IsActive || 'Inactive') === statusFilter;
                    });

                    const pagedUsers = filteredUsers.slice(
                        (pagination.pageNumber - 1) * pagination.pageSize,
                        pagination.pageNumber * pagination.pageSize
                    ).map((u, i) => ({ ...u, id: u.Id || i }));

                    const adjustedPagination = {
                        ...pagination,
                        totalRecords: filteredUsers.length,
                        totalPages: Math.ceil(filteredUsers.length / pagination.pageSize) || 1
                    };

                    return (
                        <CommonTable loading={loading}
                            tableHeading="Users List"
                            headers={tableHeaders}
                            data={pagedUsers}
                            renderCell={(key, row) => renderTableCell(key, row, pagedUsers.findIndex(u => u.id === row.id))}
                            headerAction={tableActions}
                            pagination={adjustedPagination}
                            defaultExpanded={false}
                            isRowExpandable={(row) => row.RoleId === 6 || row.roleId === 6}
                            expandedRowRender={(row) => <ExpandedTenancyRow row={row} fetchTenancyData={fetchTenancyData} />}
                            onPageChange={onPageChange}
                            onLimitChange={handleLimitChange}
                            hideScrollbar={false}
                            isLoading={loading}
                            onEdit={(row) => handleEdit(row)}
                            onDelete={(row) => setDeleteModal({ isOpen: true, userId: row.Id, userName: `${row.FirstName} ${row.LastName}` })}
                            customAction={{
                                label: "Manage Tenancy",
                                icon: faFileContract,
                                onClick: async (row) => {
                                    const tData = await fetchTenancyData(row.UnitId || row.unitId);
                                    navigate("/zentcomm/user-management/manage-tenancy", {
                                        state: {
                                            tenantUserId: row.Id,
                                            siteId: row.siteId || storageService.getItem("siteId") || 2,
                                            userData: row,
                                            tenancyData: tData
                                        }
                                    });
                                },
                                disabled: (row) => row.RoleId !== 6 && row.roleId !== 6
                            }}
                        />
                    );
                })()}
            </div>


            {deleteModal.isOpen && (
                <DeleteModal
                    isOpen={deleteModal.isOpen}
                    onClose={() => setDeleteModal({ isOpen: false, userId: null, userName: "" })}
                    onConfirm={confirmDelete}
                    title="Delete User"
                    message={`Are you sure you want to delete ${deleteModal.userName}? This action cannot be undone.`}
                />
            )}

        </div>
    );
};

export default ManageUsers;
